/**
 * Daily Motivation Popup
 * Shows once per day on dashboard load. Personalized headline + CTA + next goal.
 * Dismissible via × button or outside click. Stores dismissal in localStorage with date key.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Flame, Trophy, Target, ArrowRight, Crown, AlertTriangle, Sparkles } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const todayKey = () => `motivationSeen_${new Date().toISOString().slice(0, 10)}`;

const VARIANT_THEME = {
  streak_risk: {
    bg: 'from-red-500 via-orange-500 to-amber-500',
    accent: 'text-red-100',
    icon: AlertTriangle,
    iconColor: 'text-yellow-300'
  },
  rival_chase: {
    bg: 'from-purple-600 via-fuchsia-500 to-pink-500',
    accent: 'text-purple-100',
    icon: Target,
    iconColor: 'text-amber-300'
  },
  podium: {
    bg: 'from-amber-500 via-yellow-500 to-orange-400',
    accent: 'text-amber-100',
    icon: Crown,
    iconColor: 'text-white'
  },
  top10: {
    bg: 'from-blue-600 via-indigo-600 to-violet-600',
    accent: 'text-blue-100',
    icon: Trophy,
    iconColor: 'text-amber-300'
  },
  first_lab: {
    bg: 'from-emerald-500 via-teal-500 to-cyan-500',
    accent: 'text-emerald-100',
    icon: Sparkles,
    iconColor: 'text-white'
  },
  onboarding: {
    bg: 'from-emerald-500 via-teal-500 to-cyan-500',
    accent: 'text-emerald-100',
    icon: Sparkles,
    iconColor: 'text-white'
  },
  progress: {
    bg: 'from-indigo-600 via-blue-600 to-cyan-500',
    accent: 'text-blue-100',
    icon: Flame,
    iconColor: 'text-orange-300'
  }
};

export const DailyMotivationPopup = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      // Already shown today?
      if (localStorage.getItem(todayKey())) return;
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        const res = await fetch(`${API_BASE}/api/leaderboard/daily-motivation`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const json = await res.json();
        if (mounted) {
          setData(json);
          // Slight delay so dashboard renders first
          setTimeout(() => mounted && setOpen(true), 800);
        }
      } catch (e) {
        console.error('Motivation fetch failed', e);
      }
    };
    check();
    return () => { mounted = false; };
  }, []);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem(todayKey(), '1');
  };

  const handleCTA = () => {
    dismiss();
    if (data?.cta_path) navigate(data.cta_path);
  };

  if (!open || !data) return null;

  const theme = VARIANT_THEME[data.variant] || VARIANT_THEME.progress;
  const Icon = theme.icon;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={dismiss}
      data-testid="motivation-popup-backdrop"
    >
      <div
        className="relative w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="motivation-popup"
      >
        {/* Hero gradient header */}
        <div className={`bg-gradient-to-br ${theme.bg} p-7 relative overflow-hidden`}>
          <div className="absolute -top-20 -right-20 w-56 h-56 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-white/10 rounded-full blur-2xl" />

          <button
            onClick={dismiss}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur flex items-center justify-center transition"
            aria-label="Close"
            data-testid="motivation-popup-close"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          <div className="relative">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                <Icon className={`w-6 h-6 ${theme.iconColor}`} />
              </div>
              <p className={`text-xs font-semibold uppercase tracking-wider ${theme.accent}`}>
                Daily check-in
              </p>
            </div>
            <h2 className="text-2xl font-bold text-white leading-tight mb-2" data-testid="motivation-headline">
              {data.headline}
            </h2>
            <p className={`text-sm ${theme.accent} leading-relaxed`}>
              {data.message}
            </p>
          </div>
        </div>

        {/* Stats grid */}
        <div className="bg-white p-6">
          {data.rank && (
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="text-center bg-slate-50 rounded-xl p-3">
                <div className="text-2xl font-bold text-slate-900">#{data.rank}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                  {data.tier?.current?.name || 'Rank'}
                </div>
              </div>
              <div className="text-center bg-orange-50 rounded-xl p-3">
                <div className="text-2xl font-bold text-orange-600 flex items-center justify-center gap-1">
                  <Flame className="w-5 h-5" />{data.streak_days}d
                </div>
                <div className="text-[10px] uppercase tracking-wider text-orange-700 mt-1">streak</div>
              </div>
              <div className="text-center bg-emerald-50 rounded-xl p-3">
                <div className="text-2xl font-bold text-emerald-600">{data.labs_completed}</div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 mt-1">labs</div>
              </div>
            </div>
          )}

          {/* Next goal progress */}
          {data.next_goal && (
            <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{data.next_goal.icon || '🎯'}</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{data.next_goal.label}</p>
                    <p className="text-xs text-slate-500">
                      {data.next_goal.remaining} more to unlock
                    </p>
                  </div>
                </div>
                <span className="text-xs font-bold text-violet-700 bg-white px-2 py-1 rounded-md">
                  {data.next_goal.current}/{data.next_goal.target}
                </span>
              </div>
              <div className="w-full h-2 bg-white rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, (data.next_goal.current / data.next_goal.target) * 100)}%`
                  }}
                />
              </div>
            </div>
          )}

          {/* Rival callout */}
          {data.rival && data.rival.labs_ahead > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                #{data.rival.rank}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-900">{data.rival.display_name}</p>
                <p className="text-xs text-amber-700">
                  {data.rival.labs_ahead} {data.rival.labs_ahead === 1 ? 'lab' : 'labs'} ahead of you
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={dismiss}
              className="px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-100 font-medium text-sm transition"
              data-testid="motivation-popup-skip"
            >
              Skip
            </button>
            <button
              onClick={handleCTA}
              className={`flex-1 px-4 py-3 rounded-xl bg-gradient-to-r ${theme.bg} text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-[1.02] transition flex items-center justify-center gap-2`}
              data-testid="motivation-popup-cta"
            >
              {data.cta_label || 'Continue'} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
